import { FormControl, FormHelperText, MenuItem, TextField } from "@mui/material"
import { checkTouched, isTouched as hasBeenTouched } from "form/tools/validation"
import { Field, FieldProps } from "formik"
import React from "react"

interface IFieldSelectProps<T extends string | number> {
  fieldName: string // ex: "main.order_id"
  label: string
  options: T[]
  value: T
  disabled?: boolean
  isReadOnly?: boolean
  sx?: any
  error?: string
  required?: boolean
  optionLabelFn(value: T): string
  processValueOnChange?: (value: T) => any
}

export const FieldSelect = React.memo(
  ({
    fieldName,
    label,
    options,
    value,
    disabled,
    isReadOnly,
    error,
    sx,
    required,
    optionLabelFn,
    processValueOnChange
  }: IFieldSelectProps<string | number>): JSX.Element => (
    <Field name={fieldName}>
      {({ form: { touched, setFieldValue } }: FieldProps) => {
        const isTouched: boolean = hasBeenTouched(touched, fieldName)
        const isDisabled: boolean = disabled || Boolean(isReadOnly)
        const hasError = isTouched ? error !== undefined && error !== "" : false
        const processValue = processValueOnChange ? processValueOnChange : (val: string | number) => val
        return (
          <FormControl id={fieldName} error={hasError}>
            <TextField
              id={`${fieldName}-text-field`}
              name={fieldName}
              variant="outlined"
              select
              fullWidth
              size="small"
              sx={{ ...sx }}
              label={label}
              value={value}
              onChange={event => {
                checkTouched(touched, fieldName)
                setFieldValue(
                  fieldName,
                  processValue(typeof options[0] === "number" ? Number(event.target.value) : event.target.value)
                )
              }}
              required={required}
              disabled={isDisabled}
              error={hasError}
              onBlur={() => {
                if (!isTouched) {
                  checkTouched(touched, fieldName)
                  setFieldValue(fieldName, processValue(value))
                }
              }}>
              {options.map((o, index) => (
                <MenuItem id={`${fieldName}-text-field-option-${index}`} key={index} value={o}>
                  {optionLabelFn(o)}
                </MenuItem>
              ))}
            </TextField>
            {hasError && <FormHelperText id={`${fieldName}-error`}>{error}</FormHelperText>}
          </FormControl>
        )
      }}
    </Field>
  )
)
